import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowRight, Upload, CheckCircle2, Send, Paperclip, X, FileText,
  Users, Briefcase, Building2, Heart, Star, TrendingUp
} from "lucide-react";
import { getIcon, track } from "@/components/landing/shared";
import { getImageUrl } from "@/utils/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_AREAS = [
  { title: "Taller y mecanica", icon: "wrench", description: "Tecnicos de mantenimiento, diagnosis y reparacion." },
  { title: "Chapa y pintura", icon: "paintbrush", description: "Chapistas, pintores y preparadores." },
  { title: "Recambios", icon: "package", description: "Gestion de stock, logistica y almacen." },
  { title: "Recepcion y atencion al cliente", icon: "users", description: "Asesores de servicio, recepcion y atencion telefonica." },
  { title: "Ventas de Usados y Km0", icon: "car", description: "Asesores comerciales y gestion de stock." },
  { title: "Administracion y soporte", icon: "file", description: "Contabilidad, RRHH, IT y gestion." },
];

const DEFAULT_REASONS = [
  { title: "Empresa consolidada", description: "Mas de 40 anos de trayectoria en el sector.", icon: "building" },
  { title: "Entorno profesional", description: "Instalaciones modernas y herramientas de ultima generacion.", icon: "shieldcheck" },
  { title: "Trabajo en equipo", description: "Un equipo unido donde cada persona cuenta.", icon: "users" },
  { title: "Contacto real con el cliente", description: "Relacion directa y trato cercano con cada cliente.", icon: "heart" },
  { title: "Posibilidad de crecimiento", description: "Plan de desarrollo y formacion continua.", icon: "target" },
  { title: "Centros en Móstoles y Madrid", description: "Dos ubicaciones con excelente acceso.", icon: "mappin" },
];

export default function TrabajaPage() {
  const [content, setContent] = useState(null);
  const [config, setConfig] = useState(null);
  const [centers, setCenters] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", center: "", area: "", position: "", experience: "", message: "", privacy: false });
  const [cv, setCv] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const cvRef = useRef(null);
  const attRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/site-content`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/config`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/centers?active_only=true`).then(r => r.data).catch(() => []),
    ]).then(([cnt, cfg, ctr]) => { setContent(cnt); setConfig(cfg); setCenters(ctr); });
  }, []);

  if (!content || !config) return <div className="flex items-center justify-center min-h-screen bg-white"><div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full" /></div>;

  const cn = content.trabaja || {};
  const hero = cn.hero || {};
  const quien = cn.quien_buscamos || {};
  const areasSec = cn.areas || {};
  const porqueSec = cn.por_que || {};
  const formCfg = cn.form || {};
  const ctaFinal = cn.cta_final || {};
  const seo = cn.seo || {};
  const companyName = config.company_name || "ASCAUTO";
  const areas = (areasSec.items || DEFAULT_AREAS);
  const reasons = (porqueSec.items || DEFAULT_REASONS);

  const handleCv = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) { toast.error("Formato de CV no permitido. Usa PDF, DOC o DOCX."); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("El CV supera 15MB"); return; }
    setCv(f);
  };

  const handleAtt = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("El archivo supera 15MB"); return; }
    setAttachment(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nombre y email son obligatorios"); return; }
    if (!cv) { toast.error("El curriculum es obligatorio"); return; }
    if (!form.privacy) { toast.error("Debes aceptar la politica de privacidad"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("center", form.center);
      fd.append("area", form.area);
      fd.append("position", form.position);
      fd.append("experience", form.experience);
      fd.append("message", form.message);
      fd.append("privacy_accepted", "true");
      fd.append("cv", cv);
      if (attachment) fd.append("attachment", attachment);
      await axios.post(`${API}/api/job-applications/submit`, fd);
      setSent(true);
      track("job_application_submit", form.area);
      toast.success("Candidatura enviada correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar la candidatura");
    }
    setSending(false);
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-white" data-testid="trabaja-page">
      <title>{seo.title || `Trabaja con nosotros | ${companyName}`}</title>
      <meta name="description" content={seo.description || `Unete al equipo de ${companyName}. Envianos tu candidatura.`} />

      {/* HERO */}
      <section className="relative min-h-[55vh] flex items-center overflow-hidden" data-testid="trabaja-hero">
        <div className="absolute inset-0">
          {hero.image_url ? <img src={getImageUrl(hero.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-slate-900" />}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 to-slate-900/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 max-w-[40px] bg-red-600" />
              <span className="text-red-400 text-[11px] font-semibold uppercase tracking-[0.3em]">{hero.label || "Empleo"}</span>
            </div>
            <h1 className="heading-industrial text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[0.95] mb-6" data-testid="trabaja-h1">
              {hero.title || "Trabaja con nosotros"}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mb-8">
              {hero.subtitle || "Buscamos personas con actitud, compromiso y ganas de crecer en un proyecto serio dentro del sector de automocion."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={scrollToForm} className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-12 px-8">
                <Send className="w-4 h-4 mr-2" /> {hero.cta_primary || "Enviar candidatura"}
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10" />
      </section>

      {/* QUIEN BUSCAMOS */}
      <section className="py-20 lg:py-28" data-testid="trabaja-quien">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4"><div className="w-8 h-1 bg-red-600" /><span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">{quien.label || "Perfiles"}</span></div>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mb-6">{quien.title || "Personas que sumen de verdad"}</h2>
              <div className="space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed">
                {(quien.text || "Buscamos perfiles que quieran trabajar con responsabilidad, actitud positiva y compromiso con el cliente. Nos interesan tanto perfiles tecnicos como personas orientadas a atencion, gestion y soporte.").split("\n").map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            {quien.image_url ? (
              <div className="aspect-[4/3] overflow-hidden"><img src={getImageUrl(quien.image_url)} alt="" className="w-full h-full object-cover" /></div>
            ) : (
              <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center"><Users className="w-24 h-24 text-slate-200" /></div>
            )}
          </div>
        </div>
      </section>

      {/* AREAS */}
      {areas.length > 0 && (
        <section className="py-20 lg:py-28 bg-slate-50" data-testid="trabaja-areas">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">{areasSec.label || "Areas profesionales"}</span>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mt-3">{areasSec.title || "Areas en las que puedes encajar"}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {areas.map((a, i) => {
                const Icon = getIcon(a.icon);
                return (
                  <div key={i} className="group border border-slate-200 bg-white p-8 hover:border-red-200 hover:shadow-lg transition-all" data-testid={`area-card-${i}`}>
                    <div className="w-14 h-14 bg-red-50 group-hover:bg-red-600 flex items-center justify-center mb-6 transition-colors"><Icon className="w-7 h-7 text-red-600 group-hover:text-white transition-colors" /></div>
                    <h3 className="heading-industrial text-base font-bold text-slate-900 uppercase mb-2">{a.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{a.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* POR QUE */}
      {reasons.length > 0 && (
        <section className="py-20 lg:py-28" data-testid="trabaja-porque">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">{porqueSec.label || "Ventajas"}</span>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mt-3">{porqueSec.title || `Por que unirte a ${companyName}`}</h2>
              {porqueSec.subtitle && <p className="text-sm text-slate-500 mt-3 max-w-xl mx-auto">{porqueSec.subtitle}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reasons.map((r, i) => {
                const Icon = r.icon ? getIcon(r.icon) : CheckCircle2;
                return (
                  <div key={i} className="flex gap-5 p-6 bg-slate-50 border border-slate-200 hover:shadow-md transition-all" data-testid={`porque-item-${i}`}>
                    <div className="w-12 h-12 bg-red-50 flex items-center justify-center flex-shrink-0"><Icon className="w-6 h-6 text-red-600" /></div>
                    <div><h3 className="heading-industrial text-sm font-bold text-slate-900 uppercase mb-1">{r.title}</h3><p className="text-sm text-slate-500">{r.description}</p></div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FORM */}
      <section ref={formRef} className="py-20 lg:py-28 bg-slate-900 relative overflow-hidden" data-testid="trabaja-form-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,.07) 0 1px, transparent 1px 60px)" }} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-red-400 text-[11px] font-semibold uppercase tracking-[0.25em]">{formCfg.label || "Candidatura"}</span>
            <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-white uppercase mt-3">{formCfg.title || "Envianos tu candidatura"}</h2>
            <p className="text-sm text-slate-400 mt-3">{formCfg.subtitle || "Cuentanos quien eres, que experiencia tienes y en que area te gustaria trabajar."}</p>
          </div>

          {sent ? (
            <div className="bg-white/5 border border-white/10 p-8 text-center" data-testid="trabaja-success">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="heading-industrial text-xl font-bold text-white uppercase mb-2">Candidatura enviada</h3>
              <p className="text-sm text-slate-400">{formCfg.success_text || "Hemos recibido tu candidatura. Si tu perfil encaja, nos pondremos en contacto contigo."}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="trabaja-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Nombre y apellidos *</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required className="rounded-none border-slate-600 bg-white/5 text-white h-11 placeholder:text-slate-500" data-testid="trabaja-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Telefono</Label>
                  <Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className="rounded-none border-slate-600 bg-white/5 text-white h-11 placeholder:text-slate-500" data-testid="trabaja-phone" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} required className="rounded-none border-slate-600 bg-white/5 text-white h-11 placeholder:text-slate-500" data-testid="trabaja-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Centro de preferencia</Label>
                  <Select value={form.center} onValueChange={v => setForm(p => ({...p, center: v}))}>
                    <SelectTrigger className="rounded-none border-slate-600 bg-white/5 text-white h-11" data-testid="trabaja-center"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>{centers.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}<SelectItem value="Indiferente">Indiferente</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Area de interes</Label>
                  <Select value={form.area} onValueChange={v => setForm(p => ({...p, area: v}))}>
                    <SelectTrigger className="rounded-none border-slate-600 bg-white/5 text-white h-11" data-testid="trabaja-area"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>{areas.map(a => <SelectItem key={a.title} value={a.title}>{a.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Puesto al que aspiras</Label>
                <Input value={form.position} onChange={e => setForm(p => ({...p, position: e.target.value}))} className="rounded-none border-slate-600 bg-white/5 text-white h-11 placeholder:text-slate-500" placeholder="Ej: Tecnico de mecanica" data-testid="trabaja-position" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Experiencia breve</Label>
                <Textarea value={form.experience} onChange={e => setForm(p => ({...p, experience: e.target.value}))} rows={3} className="rounded-none border-slate-600 bg-white/5 text-white placeholder:text-slate-500" data-testid="trabaja-experience" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Mensaje</Label>
                <Textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} rows={3} className="rounded-none border-slate-600 bg-white/5 text-white placeholder:text-slate-500" data-testid="trabaja-message" />
              </div>

              {/* CV Upload */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Curriculum vitae * <span className="text-slate-500 normal-case">(PDF, DOC, DOCX — max 15MB)</span></Label>
                {cv ? (
                  <div className="flex items-center gap-2 p-3 border border-green-500/30 bg-green-500/5">
                    <FileText className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-white flex-1 truncate">{cv.name}</span>
                    <span className="text-[10px] text-slate-500">{(cv.size / 1024 / 1024).toFixed(1)}MB</span>
                    <button type="button" onClick={() => { setCv(null); if (cvRef.current) cvRef.current.value = ""; }} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-600 p-6 text-center hover:border-red-500/50 transition-colors cursor-pointer" onClick={() => cvRef.current?.click()} data-testid="trabaja-cv-dropzone">
                    <Upload className="w-6 h-6 text-slate-500 mx-auto mb-2" />
                    <span className="text-xs text-slate-400">Haz clic para subir tu CV</span>
                  </div>
                )}
                <input ref={cvRef} type="file" className="hidden" onChange={handleCv} accept=".pdf,.doc,.docx" data-testid="trabaja-cv-input" />
              </div>

              {/* Extra attachment */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Archivo adicional <span className="text-slate-500 normal-case">(opcional)</span></Label>
                {attachment ? (
                  <div className="flex items-center gap-2 p-3 border border-slate-600 bg-white/5">
                    <Paperclip className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-white flex-1 truncate">{attachment.name}</span>
                    <button type="button" onClick={() => { setAttachment(null); if (attRef.current) attRef.current.value = ""; }} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-700 p-4 text-center hover:border-slate-500 transition-colors cursor-pointer" onClick={() => attRef.current?.click()}>
                    <Paperclip className="w-5 h-5 text-slate-600 mx-auto mb-1" />
                    <span className="text-xs text-slate-500">Carta de presentacion u otro documento (opcional)</span>
                  </div>
                )}
                <input ref={attRef} type="file" className="hidden" onChange={handleAtt} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" data-testid="trabaja-att-input" />
              </div>

              {/* Privacy */}
              <div className="pt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.privacy} onChange={e => setForm(p => ({...p, privacy: e.target.checked}))} className="mt-1 accent-red-600" data-testid="trabaja-privacy" />
                  <span className="text-xs text-slate-400">He leido y acepto la <Link to="/politica-de-privacidad" className="text-red-400 underline" target="_blank">politica de privacidad</Link> *</span>
                </label>
              </div>

              <Button type="submit" disabled={sending} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-14" data-testid="trabaja-submit">
                {sending ? "Enviando..." : <><Send className="w-4 h-4 mr-2" /> Enviar candidatura</>}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-24 lg:py-32 bg-white" data-testid="trabaja-cta-final">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-red-600 flex items-center justify-center mx-auto mb-8"><Briefcase className="w-8 h-8 text-white" /></div>
          <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mb-5">{ctaFinal.title || "Queremos conocer tu perfil"}</h2>
          <p className="text-slate-500 mb-12 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">{ctaFinal.subtitle || "Si crees que puedes aportar valor a Ascauto, estaremos encantados de recibir tu candidatura."}</p>
          <Button onClick={scrollToForm} className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-14 px-12 shadow-lg shadow-red-600/30" data-testid="trabaja-cta-btn">
            {ctaFinal.button_text || "Enviar candidatura"} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
